<template>
  <v-layout row justify-center align-center>
    <v-flex xs12>
      <v-card>
        <errors-list :errorMessage="error.message" />
        <v-form ref="form" @submit.prevent="onSubmit" color="green">
          <v-card-title class="headline">{{ $t('@manualReminderForm/sendToHeader') }}</v-card-title>
          <v-card-text>
            <v-radio-group v-model="messageType">
              <v-layout row wrap>
                <v-flex sm6 px-2 mb-4>
                  <v-layout column>
                    <v-radio :label="$t('@manualReminderForm/sms')" value="sms" />
                    <v-text-field
                      v-model="form.sms"
                      :label="$t('@manualReminderForm/phoneNumber')"
                      :placeholder="customer.mobileNumber"
                      :disabled="!smsEnabled"
                      :error-messages="validationError(error.field, errorFieldMap.mobileNumber)"
                      @keypress="onKeyPress"
                      class="mt-2"
                    />
                  </v-layout>
                </v-flex>

                <v-flex sm6 px-2 pb-1>
                  <v-layout column>
                    <v-radio :label="$t('@manualReminderForm/email')" value="email" />
                    <v-text-field
                      v-model="form.email"
                      :label="$t('@manualReminderForm/emailAddress')"
                      :placeholder="customer.email"
                      :disabled="!emailEnabled"
                      :error-messages="validationError(error.field, errorFieldMap.emailAddress)"
                      class="mt-2"
                    />
                  </v-layout>
                </v-flex>

                <v-flex xs12>
                  <v-layout justify-center></v-layout>
                </v-flex>
              </v-layout>
            </v-radio-group>
          </v-card-text>

          <v-card-actions>
            <v-btn color="red darken-1" flat @click.stop="closeModal()">
              {{ $t('@manualReminderForm/cancelButtonLabel') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn primary flat :disabled="!messageType || submitting" type="submit">
              {{ $t('@manualReminderForm/sendButtonLabel') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { onKeyPress, emailRegexp, phoneRegexp, phonePrefixHardcode } from '../../utils';
import { validationError, getErrorObject } from '../../errorHandlers';
import ErrorsList from '../../components/ErrorsList';

const errorFieldMap = {
  mobileNumber: 'mobileNumber',
  emailAddress: 'emailAddress',
};

const messageType = {
  sms: 'sms',
  email: 'email',
};

export default {
  components: { ErrorsList },
  props: {
    contract: {
      type: Object,
    },
  },

  data() {
    return {
      submitting: false,
      messageType: null,
      form: {
        [messageType.sms]: '',
        [messageType.email]: '',
      },
      error: {
        message: '',
        field: '',
      },
      onKeyPress,
      validationError,
      errorFieldMap,
    };
  },

  computed: {
    customer() {
      const contactCustomer = this.contract.customers.find((x) => !!x.isContactPerson) || {};
      return {
        ...contactCustomer,
        mobileNumber: (contactCustomer.mobileNumber || '').replace(phonePrefixHardcode, '0'),
      };
    },

    smsEnabled() {
      return this.messageType === messageType.sms;
    },

    emailEnabled() {
      return this.messageType === messageType.email;
    },
  },

  methods: {
    closeModal(isSent = false) {
      this.reset();
      this.$emit('dismiss', isSent);
    },

    async onSubmit() {
      this.error = { message: '', field: '' };

      if (this.emailEnabled && !emailRegexp.test(this.form.email)) {
        this.error = {
          message: this.$t('@manualReminderForm/invalidEmailAddressError'),
          field: errorFieldMap.emailAddress,
        };
      } else if (this.smsEnabled && !phoneRegexp.test(this.form.sms)) {
        this.error = {
          message: this.$t('@manualReminderForm/invalidPhoneNumberError'),
          field: errorFieldMap.mobileNumber,
        };
      }

      if (!this.error.message) {
        await this.sendReminder(this.form[this.messageType], this.messageType);
      }
    },

    async sendReminder(to, messageType) {
      const data = { messageType, to };

      try {
        this.error.field = '';
        this.submitting = true;
        await this.$http.post(`/api/insurances/${this.contract.id}/notification/reminder`, data);
        this.closeModal();
      } catch (e) {
        const { errorField: field, errorMessage: message } = getErrorObject(e);
        this.error = { field, message };
      } finally {
        this.submitting = false;
      }
    },

    reset() {
      this.error = { message: '', field: '' };
      this.messageType = null;
      this.form.email = this.customer.email || '';
      this.form.sms = this.customer.mobileNumber || '';
    },
  },

  mounted() {
    this.reset();
  },
};
</script>

<style scoped>
table.v-table tbody td.column-title {
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.54) !important;
}
</style>
